import { Box, SxProps, Tab, Tabs, Theme, ThemeProvider, debounce } from '@mui/material';
import { t } from 'i18next';
import { isEqual } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UrlHelper } from '../../common/utils/UrlHelper';
import { Route, TabNameLocalizationKeys } from '../../routing/routes';
import { HIDDEN_TAB_STYLES } from '../common/tab-styles';
import { ActionMenuTab } from './ActionMenuTab';
import { TestValueWrapper } from '../../common/utils/TestValueWrapper';

type NavTabsProps = {
  tabs: Route[];
  theme: Theme;
  sx?: SxProps;
};

const getElementWidth = (element: HTMLElement | null) => {
  return element?.getBoundingClientRect()?.width || 0;
};

const getActiveTabIndex = (tabs: Route[]): number => {
  return tabs.findIndex((tab) => !!tab.nestedPaths?.find((t) => UrlHelper.isCurrentPath(t)) || UrlHelper.isCurrentPath(tab.path));
};

// Avoid trashing the browser by debouncing the width computations on resize
const RESIZE_DEBOUNCE_TIME = 100;

export const NavTabs = memo(({ sx, theme, tabs: propTabs }: NavTabsProps) => {
  const { pathname } = useLocation();

  const tabContainerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const moreTabRef = useRef<HTMLAnchorElement | null>(null);

  const [tabs, setTabs] = useState(propTabs);
  const [tabIndex, setTabIndex] = useState(getActiveTabIndex(tabs));
  const [visibleTabsCount, setVisibleTabsCount] = useState(tabs.length);

  // If the active tab is in the More menu there is one tab more than `tabs`
  // as the last element, which is the active one
  const tabsValue = tabIndex >= visibleTabsCount ? tabs.length : tabIndex;

  // In case of direct access to a path that is not available as a tab
  // (because it should not be accessible), `getActiveTabIndex` would return
  // -1, which is not a valid value for the `Tabs` component.
  // Unselect tabs by setting its value to `false`, the next redirection to
  // an accessible path will select the correct value
  const safeTabsValue = tabsValue === -1 ? false : tabsValue;

  // The More tab is displayed when the total tabs exceed the visible tabs and
  // the settings tab is disabled
  const moreTabDisplayed = tabs.length > visibleTabsCount;

  const moreTabs = useMemo(() => {
    const moreTabItems = tabs.slice(visibleTabsCount, tabs.length);
    const settingsTab = moreTabItems.find((tab) => tab.name === t(TabNameLocalizationKeys.SETTINGS));

    if (settingsTab) {
      return moreTabItems.filter((tab) => tab !== settingsTab).concat(settingsTab.children!);
    }
    return moreTabItems;
  }, [tabs, visibleTabsCount]);

  const resize = useCallback(() => {
    // Prevent resizing during tests. JSDOM does not actually render elements,
    // so each width would be 0, resulting in no tab being selected ever
    if (TestValueWrapper.wrap(false, true)) return;

    if (tabs.length === 0 || !tabContainerRef.current || tabRefs.current.length === 0 || !moreTabRef.current) return;

    const availableWidth = getElementWidth(tabContainerRef.current);
    const moreTabWidth = getElementWidth(moreTabRef.current);

    let usedWidth = 0;
    let fitTabs = 0;

    // Compute how many tabs fit in the current width
    for (const tab of tabRefs.current) {
      const tabWidth = getElementWidth(tab);
      if (usedWidth + tabWidth < availableWidth) {
        usedWidth += tabWidth;
        fitTabs++;
      }
    }

    // If not all tabs fit, make sure the more tab fits by removing other tabs
    if (fitTabs > 0 && fitTabs < tabs.length) {
      while (usedWidth + moreTabWidth > availableWidth) {
        fitTabs--;
        usedWidth -= getElementWidth(tabRefs.current[fitTabs]);
      }
    }

    setVisibleTabsCount(fitTabs);
  }, [tabs]);

  useEffect(() => {
    // Tabs are passed by outer components that may or may not memoize them.
    // The reference to the tab array may then change even if its content does not.
    // Only update the internal `tabs` if the content of the `propTabs` change
    // to prevent unnecessary re-renders and dependency changes
    if (!isEqual(propTabs, tabs)) {
      setTabs(propTabs);
    }
  }, [tabs, propTabs]);

  useEffect(() => {
    setTabIndex(getActiveTabIndex(tabs));
  }, [tabs, pathname]);

  useEffect(() => {
    const debouncedResize = debounce(resize, RESIZE_DEBOUNCE_TIME);

    resize();
    window.addEventListener('resize', debouncedResize);

    return () => {
      debouncedResize.clear();
      window.removeEventListener('resize', debouncedResize);
    };
  }, [resize]);

  return (
    <Box ref={tabContainerRef} sx={sx}>
      <ThemeProvider theme={theme}>
        <Tabs value={safeTabsValue}>
          {tabs.map((tab, index) => {
            const name = tab.name;
            if (name === t(TabNameLocalizationKeys.SETTINGS)) {
              const children = tab.children!;
              return (
                <ActionMenuTab
                  id={`${name}-menu`}
                  key={name}
                  label={name}
                  className={index >= visibleTabsCount ? 'hidden' : ''}
                  ref={(element) => (tabRefs.current[index] = element)}
                  theme={theme}
                  tabs={children}
                  activeTab={children[getActiveTabIndex(children)]}
                  aria-hidden={index >= visibleTabsCount}
                  disabled={index >= visibleTabsCount}
                />
              );
            }
            return (
              <Tab
                key={name}
                label={name}
                className={index >= visibleTabsCount ? 'hidden' : ''}
                ref={(element) => (tabRefs.current[index] = element)}
                sx={HIDDEN_TAB_STYLES}
                href={tab.path}
                aria-hidden={index >= visibleTabsCount}
                disabled={index >= visibleTabsCount}
              />
            );
          })}

          <ActionMenuTab
            ref={moreTabRef}
            label={t('more')}
            className={moreTabDisplayed ? '' : 'hidden'}
            theme={theme}
            tabs={moreTabs}
            activeTab={moreTabs[getActiveTabIndex(moreTabs)]}
            aria-hidden={!moreTabDisplayed}
            disabled={!moreTabDisplayed}
          />
        </Tabs>
      </ThemeProvider>
    </Box>
  );
});
